/**
 * Section « Catégories & morceaux ».
 *
 * Catégorie et difficulté sont décodées depuis le nom de fichier
 * (`Artiste_Titre-XY.mid`, X = difficulté, Y = catégorie), convention suivie par
 * 97,4 % des actions portant un morceau.
 */

import { CategoryChart } from '../components/CategoryChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile } from '../components/StatTile';
import { TopByCategoryTable } from '../components/TopByCategoryTable';
import { TopTracksChart } from '../components/TopTracksChart';
import { useAuth } from '../hooks/useAuth';
import { useCatalogStats } from '../hooks/usePeriodStats';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { formatInteger, formatPercent } from '../lib/format';
import { CATEGORY_EXERCISE, CATEGORY_UNCATALOGUED } from '@shared/types/stats';

export function CatalogPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();

  const { data, error, isPending, isFetching } = useCatalogStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const categories = data?.categories ?? [];
  const total = categories.reduce((sum, c) => sum + c.total, 0);
  const plays = categories.reduce((sum, c) => sum + c.plays, 0);
  const learns = categories.reduce((sum, c) => sum + c.learns, 0);
  const top = [...categories].sort((a, b) => b.total - a.total)[0];

  // Ce qui n'est pas un morceau du catalogue : exercices du Parcours et
  // fichiers importes hors convention. Les afficher separement evite de les
  // confondre avec la categorie « Importation », qui est officielle.
  const offCatalog = categories
    .filter((c) => c.categoryId === CATEGORY_EXERCISE || c.categoryId === CATEGORY_UNCATALOGUED)
    .reduce((sum, c) => sum + c.total, 0);

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Actions sur un morceau"
          value={formatInteger(total)}
          hint="écoutes et apprentissages"
        />
        <StatTile label="Écoutes" value={formatInteger(plays)} hint={formatPercent(total > 0 ? (plays / total) * 100 : 0)} />
        <StatTile label="Apprentissages" value={formatInteger(learns)} hint={formatPercent(total > 0 ? (learns / total) * 100 : 0)} />
        <StatTile
          label="Catégorie dominante"
          value={top?.label ?? '—'}
          hint={top ? formatPercent(top.share) : ''}
        />
        <StatTile
          label="Hors catalogue"
          value={formatPercent(total > 0 ? (offCatalog / total) * 100 : 0)}
          hint="exercices du Parcours et imports libres"
        />
      </div>

      <div className="chart-grid">
        <CategoryChart data={categories} />

        <TopTracksChart data={data?.topPlayed ?? []} metric="plays" />
        <TopTracksChart data={data?.topLearned ?? []} metric="learns" />

        <TopByCategoryTable data={data?.topByCategory ?? []} />
      </div>

      <p className="footnote">
        Catégorie et difficulté sont lues dans le nom de fichier
        (<code>Artiste_Titre-XY.mid</code>). « Exercices du Parcours » regroupe les accords et
        gammes, « Hors catalogue » les fichiers importés sans suivre la convention — à ne pas
        confondre avec la catégorie « Importation », qui en fait partie. Le mode Parcours
        (<code>lessons</code>) est exclu de cette section. Les comptes de test (identifiants 1 à 4)
        le sont aussi.
      </p>
    </SectionShell>
  );
}
